<template>
  <view-container>
    <browse-tabs :model-value="BROWSE_TABS.COLLECTIONS" view />

    <base-three-quarters-column col-spacing-y="0">
      <template #first>
        <view-header-breadcrumbs
          :item-type="ITEM_TYPES.COLLECTION"
          :library-breadcrumb="libraryBreadcrumb"
          :collection="collection"
        />
        <div
          class="tw-mb-5"
          :class="{ 'lg:tw-hidden': Settings.nameRightSide }"
        >
          <h3 class="tw-leading-tight tw-text-h2 tw-font-medium">
            {{ collection?.name || '&nbsp;' }}
          </h3>
          <p v-if="collection" class="tw-opacity-70">
            {{ $t('published') }}
            <base-time-tooltip :timestamp="collection.date_live">
              {{ humanDateFormat(collection.date_live) }}
            </base-time-tooltip>
            &bull;
            {{ $t('updated') }}
            <base-time-tooltip
              :timestamp="collection.date_updated || collection.date_live"
            >
              {{ timeSince(collection.date_updated || collection.date_live) }}
              {{ $t('ago') }}
            </base-time-tooltip>
          </p>
        </div>

        <div
          v-if="
            !loadingCollectionMods &&
            isFollowing &&
            collectionSubCount > 0 &&
            collectionSubCount < mods.result_total
          "
          class="tw-absolute tw-top-[7.375rem] tw-right-2 tw-z-4 tw-bg-warning tw-text-warning-text tw-text-xs tw-font-medium tw-p-2 tw-w-fit mod-global--border-radius"
          :class="{ 'lg:tw-top-2': Settings.nameRightSide }"
        >
          {{ $t('collection_incomplete_subscriptions') }}:
          {{ collectionSubCount }} /
          {{ mods.result_total }}
        </div>
      </template>

      <template #second>
        <div
          v-if="Settings.nameRightSide"
          class="tw-mb-8 tw-hidden lg:tw-block"
        >
          <h3 class="tw-leading-tight tw-text-h3 tw-mb-2 tw-font-medium">
            {{ collection?.name || '&nbsp;' }}
          </h3>
          <template v-if="collection">
            <p class="tw-opacity-70 tw-leading-none tw-mb-1">
              {{ $t('published') }}
              <base-time-tooltip :timestamp="collection.date_live">
                {{ humanDateFormat(collection.date_live) }}
              </base-time-tooltip>
            </p>
            <p class="tw-opacity-70 tw-leading-none tw-mb-8">
              {{ $t('updated') }}
              <base-time-tooltip
                :timestamp="collection.date_updated || collection.date_live"
              >
                {{ timeSince(collection.date_updated || collection.date_live) }}
                {{ $t('ago') }}
              </base-time-tooltip>
            </p>
          </template>

          <view-images
            no-margin
            :loading="loadingCollection"
            :logo-image="collection?.logo"
            :images="collectionImage"
          />
        </div>

        <follow-button
          v-if="Settings.allowSubscribing"
          :game="game"
          :collection="collection"
          class="tw-mt-4"
          :class="Settings.nameRightSide ? 'lg:tw-mt-0' : 'lg:tw-mt-[6.875rem]'"
        />

        <div
          class="tw-flex tw-flex-col tw-space-y-8"
          :class="
            Settings.allowSubscribing
              ? 'tw-mt-2'
              : Settings.nameRightSide
                ? ''
                : 'lg:tw-mt-[6.875rem]'
          "
        >
          <!-- show for lg screen down -->
          <view-collection-stats
            :panel-loading="loadingCollection"
            :collection="collection"
            :game="game"
            :class="Classes.SUMMARY"
          />

          <compatibility-panel
            v-if="!loadingCollection"
            :game="game"
            :collection="collection"
          />

          <view-tags
            v-if="tags.length && !loadingCollection"
            :class="Classes.TAGS"
            no-margin
            :tags="tags"
            @tag:filter="(input) => tagFilter(input)"
          />

          <!-- show for lg screen down -->
          <view-contributors
            :collection="collection"
            :panel-loading="loadingCollection || !collection"
            :collection-mod-contributors="contributors"
          />

          <template v-if="!collectionCurator || isGameModerator">
            <view-flag-button
              :resource="RESOURCE_COLLECTIONS"
              :resource-id="collection?.id"
              :name="collection?.name"
              :class="Classes.DETAIL_REPORT_BUTTON"
              class="tw-hidden lg:tw-block"
            />

            <view-flag-button
              :resource="RESOURCE_COLLECTIONS"
              :resource-id="collection?.id"
              :name="collection?.name"
              :class="Classes.DETAIL_REPORT_BUTTON"
              class="lg:tw-hidden"
            />
          </template>
        </div>
      </template>

      <template v-if="collection" #[discussionSlotName]>
        <p class="tw-text-h6 tw-font-medium tw-mb-4 lg:tw-mb-8">
          {{ $t('description') }}
        </p>
        <div :class="[Classes.DESCRIPTION, sectionClasses]">
          <view-description
            :mod="collection"
            :panel-loading="loadingCollection"
            collapse
          />
        </div>

        <base-switcher
          class="tw-my-4 lg:tw-mb-8 tw-text-h6"
          :options="switcherTabs"
          :model-value="tab"
          @input="(_, val) => updateTab(val)"
        />

        <div
          v-show="tab === TAB_TYPES.DISCUSSION.value"
          :class="[Classes.COMMENTS, 'tw-mt-8 lg:tw-mt-0', sectionClasses]"
        >
          <discussion-panel
            :type="DISCUSSION_TYPE_COLLECTIONS"
            :resource="collection"
            :panel-loading="loadingCollection"
            :game-name-id="game?.name_id"
            @update:count="(val) => (discussionCount = val)"
          />
        </div>

        <div
          v-show="tab === TAB_TYPES.MODS.value"
          :class="[Classes.DEPENDENCIES, 'tw-mt-8 lg:tw-mt-0', sectionClasses]"
        >
          <view-mod-list
            :collection="collection"
            :hidden-mod-count="hiddenModCount"
            :loading="loadingCollection || loadingCollectionMods"
            :mods-total="mods.result_total"
            :game="game"
            :mod-list="mods.data"
          />
          <panel-error
            v-if="tab === TAB_TYPES.MODS.value && collectionModsError"
            :message="
              $t('error_get_mod', {
                ugc: ugcName(game.value, false, true).value,
              })
            "
          />
        </div>
      </template>
    </base-three-quarters-column>
  </view-container>
</template>

<script>
import ViewCollectionStats from '@components/Collection/ViewCollectionStats.vue'
import ViewHeaderBreadcrumbs from '@components/View/ViewHeaderBreadcrumbs.vue'
import CompatibilityPanel from '@components/Collection/CompatibilityPanel.vue'
import { getAllCollectionModsRequest, getCollectionRequest } from '@services'
import DiscussionPanel from '@components/Discussion/DiscussionPanel.vue'
import ViewModList from '@shared/components/Collection/ViewModList.vue'
import { areCollectionCommentsAllowed } from '@helpers/bitHelper.js'
import ViewContributors from '@components/Mod/ViewContributors.vue'
import FollowButton from '@components/Collection/FollowButton.vue'
import ViewDescription from '@components/Mod/ViewDescription.vue'
import ViewFlagButton from '@components/View/ViewFlagButton.vue'
import { COLLECTION_BROWSE_ROUTE } from '@config/routeNames.js'
import BrowseTabs from '@components/Navigation/BrowseTabs.vue'
import ViewContainer from '@components/View/ViewContainer.vue'
import { humanDateFormat, timeSince } from '@helpers/utils.js'
import { onBeforeMount, computed, provide, ref } from 'vue'
import PanelError from '@components/Error/PanelError.vue'
import ViewImages from '@components/View/ViewImages.vue'
import ViewTags from '@components/View/ViewTags.vue'
import { getTags } from '@helpers/localizedTags.js'
import Classes from '@shared/js/config/css.js'
import { useBreakpoints } from '@vueuse/core'
import { Settings } from '@config/game.js'
import { BREAKPOINTS } from '@config'
import { useI18n } from 'vue-i18n'
import {
  DISCUSSION_TYPE_COLLECTIONS,
  RESOURCE_COLLECTIONS,
  BROWSE_TABS,
  ITEM_TYPES,
} from '@config/options.js'
import {
  collectionFollowingStore,
  subscriptionStore,
  collectionStore,
  gameStore,
} from '@stores'
import {
  useResourceValue,
  usePermissions,
  useRoute,
  useAsync,
} from '@composables'

const sectionClasses =
  'game-tile-background-color game-tile-radius game-tile-border tw-p-4'

export default {
  components: {
    ViewHeaderBreadcrumbs,
    ViewCollectionStats,
    CompatibilityPanel,
    ViewContributors,
    DiscussionPanel,
    ViewDescription,
    ViewFlagButton,
    ViewContainer,
    FollowButton,
    ViewModList,
    PanelError,
    BrowseTabs,
    ViewImages,
    ViewTags,
  },
  emits: ['tag:filter'],
  setup() {
    const { getCollectionId, push, routerHistory, replace, routeQuery } =
      useRoute()

    const collectionNameId = getCollectionId().value
    const { greaterOrEqual } = useBreakpoints(BREAKPOINTS)
    const { followedCollections } = collectionFollowingStore()
    const { subscriptions } = subscriptionStore()
    const lgAndUp = greaterOrEqual('lg')
    const mods = ref({ data: [], result_total: 0 })
    const discussionCount = ref(0)

    const { ugcName } = useResourceValue()
    const { t } = useI18n()

    const ugcNameText = computed(() => {
      let _ugcNameText = ugcName(null, false, false).value

      if (['mods', 'levels'].includes(_ugcNameText.toLowerCase())) {
        _ugcNameText = t(_ugcNameText.split('s')[0].toLowerCase(), 2)
      }

      return _ugcNameText
    })

    // Stores
    const { addCollection, getCollectionByNameId } = collectionStore()
    const { getPublicGame } = gameStore()

    const game = getPublicGame()
    const collection = getCollectionByNameId(
      game.value.name_id,
      collectionNameId
    )

    const { isGameModerator, hasPermissionForCollection } = usePermissions()
    const collectionCurator = computed(() =>
      hasPermissionForCollection(collectionNameId, game.value.name_id)
    )

    const {
      loading: loadingCollection,
      run: runfetchCollection,
      data: collectionData,
      error: collectionError,
    } = useAsync(() => getCollectionRequest(collectionNameId))

    const {
      loading: loadingCollectionMods,
      run: runFetchCollectionMods,
      data: collectionModsData,
      error: collectionModsError,
    } = useAsync(
      (query) => getAllCollectionModsRequest(collectionNameId, query),
      `Failed to load collection ${ugcName(game.value, false, true).value}`
    )

    const TAB_TYPES = computed(() => ({
      MODS: { text: ugcNameText.value, value: 'mods' },
      DISCUSSION: { text: t('discussion'), value: 'discussion' },
    }))

    const tab = ref(routeQuery.value.tab ?? TAB_TYPES.value.MODS.value)

    const collectionImage = computed(
      () => collection.value?.logo && [collection.value.logo]
    )

    const libraryBreadcrumb = computed(() =>
      routerHistory.value.back?.includes('/library')
    )

    // TODO update with collection comment downvote setting value once available
    provide('gameAllowDownvote', { value: true })

    const contributors = computed(() =>
      mods.value.data.map((mod) => mod.submitted_by)
    )

    const discussionSlotName = computed(() =>
      lgAndUp.value ? 'first-bottom' : 'second-bottom'
    )

    const tags = computed(() =>
      collection.value && game.value
        ? getTags(
            game.value.collection_tag_options,
            isGameModerator,
            collection
          )
        : []
    )

    const collectionSubCount = computed(() => {
      let subscribeCount = 0
      let mySubscriptionIds = subscriptions.value.map((s) => s.id)

      mods.value.data.forEach((mod) => {
        if (mySubscriptionIds.includes(mod.id)) {
          subscribeCount++
        }
      })

      return subscribeCount
    })

    const isFollowing = computed(() => {
      if (followedCollections.value?.length && collection.value?.id) {
        return followedCollections.value.some(
          (subscription) => subscription?.id === parseInt(collection.value.id)
        )
      }

      return false
    })

    const showComments = computed(
      () =>
        Settings.value.showComments &&
        areCollectionCommentsAllowed(game.value.community_options) &&
        areCollectionCommentsAllowed(collection.value?.community_options)
    )

    const switcherTabs = computed(() => {
      const tabs = [
        {
          ...TAB_TYPES.value.MODS,
          count: collection.value?.stats?.mods_total || 0,
        },
        {
          ...TAB_TYPES.value.DISCUSSION,
          count: discussionCount.value,
        },
      ]

      if (!showComments.value) tabs.splice(1, 1)

      return tabs
    })

    const hiddenModCount = computed(
      () => (collection.value?.stats?.mods_total || 0) - mods.value.result_total
    )

    function tagFilter(name) {
      push({
        name: COLLECTION_BROWSE_ROUTE,
        query: { tags: name },
      })
    }

    onBeforeMount(() => {
      if (!collection.value) fetchCollection()
      fetchMods()
    })

    async function fetchCollection() {
      await runfetchCollection()

      if (!collectionError.value) {
        addCollection(game.value.name_id, collectionData.value)
      }
    }

    async function fetchMods() {
      await runFetchCollectionMods()

      if (!collectionModsError.value) {
        mods.value = collectionModsData.value
      }
    }

    function updateTab(val) {
      tab.value = val

      replace({
        query: { tab: val },
      })
    }

    return {
      DISCUSSION_TYPE_COLLECTIONS,
      loadingCollectionMods,
      RESOURCE_COLLECTIONS,
      collectionModsError,
      discussionSlotName,
      collectionSubCount,
      libraryBreadcrumb,
      loadingCollection,
      collectionCurator,
      isGameModerator,
      collectionImage,
      discussionCount,
      humanDateFormat,
      hiddenModCount,
      sectionClasses,
      switcherTabs,
      contributors,
      showComments,
      isFollowing,
      BROWSE_TABS,
      ITEM_TYPES,
      collection,
      TAB_TYPES,
      updateTab,
      timeSince,
      tagFilter,
      Settings,
      Classes,
      game,
      tags,
      mods,
      tab,
    }
  },
}
</script>
